// Unpublished Work © 2023 Deere & Company.

import {
  jdBlack,
  jdGreen,
  ThemeProvider,
  ToastProvider,
} from "@deere/fuel-react";
import React, { Suspense, useContext } from "react";
import LoadingIndicator from "../../components/LoadingIndicator/LoadingIndicator";
import { AppNotificationContextProvider } from "../../context/AppNotificationContext/AppNotificationContext";
import { AuthorizationProvider } from "../../context/AuthorizationContext";
import { ConfigContext } from "../../context/ConfigContext";
import { FeatureToggleContextV2 } from "../../context/FeatureToggleContextV2/FeatureToggleContextV2";
import { PreferenceContext } from "../../context/PreferencesContext/PreferencesContext";
import i18n from "../../i18n/i18n";
import Language from "../../i18n/Language";
import { CATEGORIES, TOGGLES } from "../../pages/featureToggles/constants";
import LocalStorage from "../../utils/LocalStorage";
import { getTheme, saveTheme, ThemeName } from "../../utils/theme";

const THEME_PREF_MAP: { [k: string]: typeof jdGreen | typeof jdBlack } = {
  deere_green: jdGreen,
  deere_yellow: jdBlack,
};

const getThemeFromThemePref = (
  themePref: string | undefined
): typeof jdGreen | typeof jdBlack => {
  if (themePref === undefined) return getTheme();
  return THEME_PREF_MAP[themePref];
};

const getLangFromLangPref = (langPref: string | undefined): string => {
  if (langPref === undefined) {
    const lang = LocalStorage.getItem("lang");
    return lang || Language.ENGLISH;
  }
  return langPref;
};

interface AppContainerPropsType {
  children: React.ReactNode;
}

// eslint-disable-next-line react/function-component-definition
const AppContainer: React.FC<AppContainerPropsType> = ({ children }) => {
  const { ENABLE_PREFERENCES } = useContext(ConfigContext);
  const { userPreferences } = useContext(PreferenceContext);
  const { isToggleEnabled } = useContext(FeatureToggleContextV2);

  const themePref = userPreferences.find(
    ({ key }) => key === "application_style"
  );
  const langPref = userPreferences.find(({ key }) => key === "language");

  const theme = ENABLE_PREFERENCES
    ? getThemeFromThemePref(themePref?.value)
    : getTheme();

  saveTheme(
    theme === jdGreen
      ? ("DeereGreen" as ThemeName)
      : ("DeereYellow" as ThemeName)
  );
  if (ENABLE_PREFERENCES) {
    const finalLang = getLangFromLangPref(langPref?.value);
    LocalStorage.setItem("lang", finalLang);
    i18n.changeLanguage(finalLang);
  }

  const isAppNotificationsEnabled = isToggleEnabled?.(
    CATEGORIES.ALERTS_SERVICE,
    TOGGLES.ENABLE_ALERTS_AND_NOTIFICATIONS
  );

  return (
    <ThemeProvider theme={theme}>
      <Suspense fallback={<LoadingIndicator />}>
        <ToastProvider
          autoHideDuration={4000}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          TransitionProps={{ direction: "top" }}
        >
          <AuthorizationProvider>
            {isAppNotificationsEnabled ? (
              <AppNotificationContextProvider>
                <>{children}</>
              </AppNotificationContextProvider>
            ) : (
              <>{children}</>
            )}
          </AuthorizationProvider>
        </ToastProvider>
      </Suspense>
    </ThemeProvider>
  );
};

export default AppContainer;
